import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { flatMap } from 'rxjs/operators';
import { BulkCreateStudentSupports, CreateStudentSupport } from 'Src/ng2/store';
import { ISchoolSupport } from '../../typings/interfaces/support.interface';
import { ApiService } from './../../services/api-service/api-service';
import { IAssignSupportModalComponentData } from './assign-support-modal.component';

/* istanbul ignore next */
@Injectable()
export class AssignSupportModalService {
  constructor(private apiService: ApiService, private store: Store<any>) {}

  readonly STUDENT_SUPPORT_PROJECTION = {
    'support.supportId': true,
  };

  readonly SUPPORTS_PROJECTION = {
    name: true,
    category: true,
    metaData: true,
    schedule: true,
    schoolId: true,
    status: true,
    location: true,
    activityLead: true,
  };

  private getStudentSupports({ schoolId, studentId }) {
    const query = {
      where: JSON.stringify({
        'student.studentId': studentId,
        status: 'ACTIVE',
      }),
    };

    const getStudentSupports$ = this.apiService.getStudentSupports(schoolId, query, this.STUDENT_SUPPORT_PROJECTION);

    return getStudentSupports$;
  }

  /**
   * TODO: once `supports` and `studentSupports` for the student profile exist on the ngrx store,
   * replace logic in `AssignSupportModalService#getSupports` to leverage the ngrx store. (CM)
   *
   */
  public getSupports(options: IAssignSupportModalComponentData) {
    const { schoolId, studentIds, category, examSubjectKey } = options;
    const supportsWhereQuery: any = {
      status: 'ACTIVE',
    };

    if (category) {
      supportsWhereQuery.category = category;

      if (examSubjectKey) {
        supportsWhereQuery.metaData = { examSubject: examSubjectKey };
      }
    }

    // if studentId, we want to filter out the supports that a student already has an active studentSupport for. (CM)
    if (studentIds.length === 1) {
      const getStudentSupports$ = this.getStudentSupports({ schoolId, studentId: studentIds[0] });

      // We first get student supports for a student to filter out supports that the student
      // has already been assigned to. (CM)
      return getStudentSupports$.pipe(
        flatMap((data: any) => {
          const studentSupports = data.data;
          const studentSupport_SupportIds = studentSupports.map(studentSupport => studentSupport.support.supportId);

          supportsWhereQuery._id = { $not: { $in: studentSupport_SupportIds } };

          const supportsQueries = {
            where: JSON.stringify(supportsWhereQuery),
            projection: JSON.stringify(this.SUPPORTS_PROJECTION),
          };
          const getPaginatedSupports$ = this.apiService.getPaginatedSupports(schoolId, supportsQueries);

          return getPaginatedSupports$;
        }),
      );
    } else {
      // return all active supports for school
      const supportsQueries = {
        where: JSON.stringify(supportsWhereQuery),
        projection: JSON.stringify(this.SUPPORTS_PROJECTION),
      };
      const getPaginatedSupports$ = this.apiService.getPaginatedSupports(schoolId, supportsQueries);

      return getPaginatedSupports$;
    }
  }

  createStudentSupport(payload: { support: ISchoolSupport; studentId: string; schoolId: string }): void {
    this.store.dispatch(new CreateStudentSupport(payload));
  }

  bulkAssignStudentSupport(payload): void {
    this.store.dispatch(new BulkCreateStudentSupports(payload));
  }
}
